import React, {useCallback, useState} from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  useWindowDimensions,
  StyleSheet,
} from 'react-native';
// Use the branch's platform shim, not react-native-linear-gradient directly:
// the native package calls requireNativeComponent, which react-native-web does
// not export, and that breaks the web build.
import LinearGradient from 'components/LinearGradient';
import {useDispatch, useSelector} from 'react-redux';

import {X, Check, Lock, Coins, Sparkles, Ban} from 'components/Icons';
import {useRemScale} from 'lib/remScale';
import {GUARDIANS, GUARDIAN_NONE_ID} from 'config/guardiansCatalog';
import {getGuardianImage} from 'images/Guardians';
import {purchaseGuardian, selectGuardian} from 'store/guardians';

const ACCENT = '#C5E836';

// Gradient pairs used for the placeholder badge shown until real thumbnails
// are dropped into src/images/Guardians.
const FALLBACK_GRADIENTS = [
  ['#7c3aed', '#4c1d95'],
  ['#0ea5e9', '#0c4a6e'],
  ['#f59e0b', '#78350f'],
  ['#10b981', '#064e3b'],
  ['#ef4444', '#7f1d1d'],
  ['#8b5cf6', '#3730a3'],
  ['#14b8a6', '#134e4a'],
  ['#f472b6', '#831843'],
  ['#eab308', '#713f12'],
];

/**
 * Guardian picker, opened from the metaverse top HUD.
 *
 * Selecting an owned guardian equips it immediately and tells Unity via
 * `onGuardianSelected`. Locked guardians are purchased with QuikCoins first;
 * all catalog prices are currently 0, so the purchase path is exercised without
 * actually costing anything.
 *
 * Props:
 * - visible: boolean
 * - onClose: () => void
 * - onGuardianSelected: (guardianId: string) => void
 * - quikcoinsBalance: number   (this branch keeps the balance in Meeting's local state)
 * - userId?: string
 */
export default function GuardianSelectionModal({
  visible,
  onClose,
  onGuardianSelected,
  quikcoinsBalance = 0,
  userId,
}) {
  const dispatch = useDispatch();
  const {rem} = useRemScale();
  const {width, height} = useWindowDimensions();

  const unlockedIds = useSelector(state => state.guardians?.unlockedIds ?? []);
  const selectedId = useSelector(
    state => state.guardians?.selectedId ?? GUARDIAN_NONE_ID,
  );

  const [busyId, setBusyId] = useState(null);
  // Confirmation / error dialog, mirroring the RewardsStore pattern on this
  // branch (react-native-toast-message and Alert are not available on web).
  const [dialog, setDialog] = useState(null);

  const modalWidth = Math.min(rem(560), width - rem(24) * 2);
  const modalMaxHeight = Math.round(height * 0.9);

  // Two columns on narrow screens, three once there's room.
  const columns = modalWidth >= rem(460) ? 3 : 2;
  const gridGap = rem(12);
  const tileWidth = Math.floor(
    (modalWidth - rem(16) * 2 - gridGap * (columns - 1)) / columns,
  );

  const equip = useCallback(
    async guardian => {
      const result = await dispatch(
        selectGuardian({userId, guardianId: guardian.id}),
      );
      if (result?.success) {
        // Tell Unity. Done here rather than in the store so redux stays free of
        // side effects on the 3D world.
        onGuardianSelected && onGuardianSelected(guardian.id);
        return true;
      }
      setDialog({
        title: 'Could not equip guardian',
        message: result?.error || 'Please try again.',
        buttons: [{label: 'OK'}],
      });
      return false;
    },
    [dispatch, onGuardianSelected, userId],
  );

  const runPurchase = useCallback(
    async guardian => {
      setBusyId(guardian.id);
      try {
        const result = await dispatch(
          purchaseGuardian({
            userId,
            guardianId: guardian.id,
            balance: quikcoinsBalance,
          }),
        );
        if (!result?.success) {
          setDialog({
            title: 'Purchase failed',
            message: result?.error || 'Please try again.',
            buttons: [{label: 'OK'}],
          });
          return;
        }
        await equip(guardian);
      } finally {
        setBusyId(null);
      }
    },
    [dispatch, equip, quikcoinsBalance, userId],
  );

  const onPressTile = useCallback(
    guardian => {
      if (busyId) return;

      if (guardian.id === selectedId) {
        onClose && onClose();
        return;
      }

      if (unlockedIds.includes(guardian.id)) {
        setBusyId(guardian.id);
        equip(guardian).finally(() => setBusyId(null));
        return;
      }

      const cost = guardian.cost || 0;
      // Free guardians don't warrant a confirmation prompt.
      if (cost <= 0) {
        runPurchase(guardian);
        return;
      }

      setDialog({
        title: `Unlock ${guardian.name}?`,
        message: `This costs ${cost} QuikCoins. You have ${quikcoinsBalance}.`,
        buttons: [
          {label: 'Cancel', variant: 'ghost'},
          {label: 'Unlock', onPress: () => runPurchase(guardian)},
        ],
      });
    },
    [busyId, equip, onClose, quikcoinsBalance, runPurchase, selectedId, unlockedIds],
  );

  const renderTile = (guardian, index) => {
    const owned = unlockedIds.includes(guardian.id);
    const isSelected = guardian.id === selectedId;
    const isNone = guardian.id === GUARDIAN_NONE_ID;
    const cost = guardian.cost || 0;
    const affordable = cost <= quikcoinsBalance;
    const image = getGuardianImage(guardian.id);
    const gradient = FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length];
    const busy = busyId === guardian.id;

    return (
      <TouchableOpacity
        key={guardian.id}
        activeOpacity={0.9}
        onPress={() => onPressTile(guardian)}
        disabled={busy}
        accessibilityRole="button"
        accessibilityLabel={
          isSelected
            ? `${guardian.name}, currently equipped`
            : owned
            ? `Equip ${guardian.name}`
            : `Unlock ${guardian.name} for ${cost} QuikCoins`
        }
        style={{
          width: tileWidth,
          marginBottom: gridGap,
          borderRadius: rem(12),
          borderWidth: isSelected ? 2 : 1,
          borderColor: isSelected ? ACCENT : 'rgba(255,255,255,0.10)',
          backgroundColor: 'rgba(255,255,255,0.04)',
          overflow: 'hidden',
          opacity: busy ? 0.6 : 1,
        }}>
        {/* Artwork */}
        <View
          style={{
            height: Math.round(tileWidth * 0.82),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.35)',
          }}>
          {isNone ? (
            <Ban size={rem(38)} color="rgba(255,255,255,0.45)" strokeWidth={1.8} />
          ) : image ? (
            <Image
              source={image}
              resizeMode="contain"
              style={{width: '100%', height: '100%'}}
            />
          ) : (
            // Placeholder until a thumbnail exists for this id.
            <LinearGradient
              colors={gradient}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 1}}
              style={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Sparkles size={rem(24)} color="rgba(255,255,255,0.75)" strokeWidth={2} />
              <Text
                style={{
                  color: '#fff',
                  fontSize: rem(20),
                  fontWeight: '800',
                  marginTop: rem(4),
                  letterSpacing: 0.5,
                }}>
                {guardian.name}
              </Text>
            </LinearGradient>
          )}

          {/* Locked scrim */}
          {!owned && !busy ? (
            <View
              style={[
                StyleSheet.absoluteFillObject,
                {
                  backgroundColor: 'rgba(0,0,0,0.45)',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              ]}>
              <Lock size={rem(22)} color="rgba(255,255,255,0.85)" strokeWidth={2.2} />
            </View>
          ) : null}

          {busy ? (
            <View
              style={[
                StyleSheet.absoluteFillObject,
                {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              ]}>
              <ActivityIndicator color={ACCENT} />
            </View>
          ) : null}

          {/* Equipped badge */}
          {isSelected ? (
            <View
              style={{
                position: 'absolute',
                top: rem(6),
                right: rem(6),
                width: rem(22),
                height: rem(22),
                borderRadius: rem(11),
                backgroundColor: ACCENT,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Check size={rem(14)} color="#0f1014" strokeWidth={3} />
            </View>
          ) : null}
        </View>

        {/* Meta */}
        <View style={{padding: rem(10)}}>
          <Text
            numberOfLines={1}
            style={{color: '#fff', fontSize: rem(14), fontWeight: '700'}}>
            {guardian.name}
          </Text>
          <Text
            numberOfLines={2}
            style={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: rem(11),
              marginTop: rem(2),
              minHeight: rem(28),
            }}>
            {guardian.description}
          </Text>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: rem(8),
            }}>
            {isSelected ? (
              <Text style={{color: ACCENT, fontSize: rem(12), fontWeight: '800'}}>
                Equipped
              </Text>
            ) : owned ? (
              <Text
                style={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: rem(12),
                  fontWeight: '700',
                }}>
                Tap to equip
              </Text>
            ) : cost <= 0 ? (
              <Text style={{color: ACCENT, fontSize: rem(12), fontWeight: '800'}}>
                Free
              </Text>
            ) : (
              <>
                <Coins
                  size={rem(13)}
                  color={affordable ? ACCENT : 'rgba(255,255,255,0.4)'}
                  strokeWidth={2.5}
                />
                <Text
                  style={{
                    marginLeft: rem(5),
                    color: affordable ? ACCENT : 'rgba(255,255,255,0.4)',
                    fontSize: rem(12),
                    fontWeight: '800',
                  }}>
                  {cost}
                </Text>
              </>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={!!visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}>
      <View
        style={[
          StyleSheet.absoluteFillObject,
          {justifyContent: 'center', alignItems: 'center'},
        ]}
        pointerEvents="box-none">
        <TouchableOpacity
          style={StyleSheet.absoluteFillObject}
          activeOpacity={1}
          onPress={onClose}
        />

        <View
          style={{
            width: modalWidth,
            maxHeight: modalMaxHeight,
            backgroundColor: 'rgba(0,0,0,0.95)',
            borderRadius: rem(12),
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.10)',
            padding: rem(16),
          }}>
          {/* Header */}
          <View style={{marginBottom: rem(12)}}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <Text style={{color: '#fff', fontSize: rem(22), fontWeight: '700'}}>
                Choose Your Guardian
              </Text>
              <TouchableOpacity
                onPress={onClose}
                accessibilityRole="button"
                accessibilityLabel="Close guardian selection"
                style={{padding: rem(8), margin: rem(-8), opacity: 0.7}}>
                <X size={rem(20)} color="#fff" strokeWidth={2.5} />
              </TouchableOpacity>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: rem(4),
              }}>
              <Text
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: rem(12),
                  flex: 1,
                }}>
                A little companion that follows you around the world.
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginLeft: rem(8),
                }}>
                <Coins size={rem(14)} color={ACCENT} strokeWidth={2.5} />
                <Text
                  style={{
                    marginLeft: rem(5),
                    color: ACCENT,
                    fontSize: rem(13),
                    fontWeight: '800',
                  }}>
                  {quikcoinsBalance}
                </Text>
              </View>
            </View>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
              }}>
              {GUARDIANS.map(renderTile)}
            </View>
          </ScrollView>

          {/* Confirm / error dialog */}
          {dialog ? (
            <View
              style={[
                StyleSheet.absoluteFillObject,
                {
                  backgroundColor: 'rgba(0,0,0,0.72)',
                  borderRadius: rem(12),
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: rem(16),
                },
              ]}>
              <View
                style={{
                  width: '100%',
                  maxWidth: rem(320),
                  backgroundColor: '#111318',
                  borderRadius: rem(10),
                  borderWidth: 1,
                  borderColor: 'rgba(255,255,255,0.12)',
                  padding: rem(16),
                }}>
                <Text
                  style={{color: '#fff', fontSize: rem(16), fontWeight: '800'}}>
                  {dialog.title}
                </Text>
                {dialog.message ? (
                  <Text
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: rem(13),
                      marginTop: rem(6),
                    }}>
                    {dialog.message}
                  </Text>
                ) : null}
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    marginTop: rem(16),
                  }}>
                  {(dialog.buttons || [{label: 'OK'}]).map(btn => (
                    <TouchableOpacity
                      key={btn.label}
                      onPress={() => {
                        setDialog(null);
                        btn.onPress && btn.onPress();
                      }}
                      style={{
                        paddingHorizontal: rem(14),
                        paddingVertical: rem(8),
                        marginLeft: rem(8),
                        borderRadius: rem(8),
                        backgroundColor:
                          btn.variant === 'ghost' ? 'transparent' : ACCENT,
                        borderWidth: btn.variant === 'ghost' ? 1 : 0,
                        borderColor: 'rgba(255,255,255,0.2)',
                      }}>
                      <Text
                        style={{
                          color: btn.variant === 'ghost' ? '#fff' : '#0f1014',
                          fontWeight: '800',
                          fontSize: rem(13),
                        }}>
                        {btn.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}
